import { useEffect, useState, type SyntheticEvent } from "react";
import type { Mode } from "src/utils/types/mode";
import type { StructEventParams } from "src/utils/types/snowplow";
import type { PressEvent } from "react-aria-components";
import type {
  PartnerPageExitCategory,
  Trigger,
  View,
} from "src/analytics/exits/generateExitPoint/generateExitPoint";
import { appendNavigationEventId } from "../url/appendNavigationEventId";
import { generateNavigationEventId } from "../../analytics/generateNavigationEventId/generateNavigationEventId";
import type { LogExitCategory } from "../../analytics/analyticsEventTypes";
import { sendAnalyticsInteractionEvent } from "../../analytics/sendAnalyticsEvent";
import { useLogExitCallback } from "./useLogExitCallback";

export type ExitLinkProps<Meta = unknown> = {
  href: string;
  kind: LogExitCategory;
  exitPoint: {
    view: View;
    trigger: Trigger;
    transitMode?: Mode;
  };
  partnerPage?: PartnerPageExitCategory;
  provider?: string;
  agencyIds?: string[];
  onExit: (event: {
    url: string;
    sendExitInteractionEvent: typeof sendAnalyticsInteractionEvent;
    /**
     * Cancels the exit and prevents the default navigation if present.
     * This is useful if you want to log the exit interaction
     * but not actually navigate away from the page until some condition is met.
     */
    preventDefaultNavigation(): void;
    meta?: Meta;
  }) => void;
};

/**
 * This hook provides a way to track external link clicks and log exit events.
 * It returns a tracked URL and a click handler that can be used in to handle
 * the click event.
 *
 * @returns [trackedUrl: string, clickHandler: (event: SyntheticEvent) => void]
 *
 * @example
 * ```typescript
 * const [trackedUrl, clickHandler] = useExitLink({
 *   href: "https://example.com",
 *   kind: "RentalCars",
 *   exitPoint: {
 *     view: "genericMap",
 *     trigger: "headerCarHire",
 *     transitMode: "car",
 *   },
 *   onExit: ({ url, sendExitInteractionEvent }) => {
 *     sendExitInteractionEvent({
 *       category: "SegmentTimeline",
 *       action: "Click:CarHire",
 *     });
 *   },
 * });
 *
 * // Usage in a component
 * return (
 *   <a href={trackedUrl} onClick={clickHandler}>
 *     Car Hire
 *   </a>
 * );
 * ```
 */
export function useExitLink<Meta = unknown>({
  href,
  provider,
  agencyIds,
  exitPoint,
  kind,
  partnerPage,
  onExit,
}: ExitLinkProps<Meta>): [
  string,
  (event: SyntheticEvent | PressEvent | undefined, meta?: Meta) => void,
] {
  const [decoratedUrl, setDecoratedUrl] = useState<string>();

  useEffect(() => {
    // Reset decorated URL when base URL changes to ensure the latest URL is used.
    setDecoratedUrl(undefined);
  }, [href]);

  const logExitCallback = useLogExitCallback(
    kind,
    exitPoint.view,
    exitPoint.trigger,
    exitPoint.transitMode,
    partnerPage,
  );

  if (!href) {
    // Early out if an empty string URL is provided.
    // This ensures we don't attempt to append an event ID to an empty URL,
    // which would lead to an invalid link.
    return ["", () => {}];
  }

  function onExitCallback(event?: SyntheticEvent | PressEvent, meta?: Meta) {
    const eventId = generateNavigationEventId();
    // Deliberately shadowing decoratedUrl to ensure we use the latest value
    // in the callback and avoid accidentally references the stale state.
    const decoratedUrl = appendNavigationEventId(href, eventId);

    const exitInteractionEventList: StructEventParams[] = [];
    function sendExitInteractionEvent(analyticsEvent: StructEventParams) {
      exitInteractionEventList.push(analyticsEvent);
    }

    let isDefaultNavigationPrevented = false;
    function preventDefaultNavigation() {
      isDefaultNavigationPrevented = true;
    }

    onExit({
      url: decoratedUrl,
      sendExitInteractionEvent,
      preventDefaultNavigation,
      meta,
    });

    if (import.meta.env.DEV) {
      validateExitEvents(exitInteractionEventList);
    }

    // Handle event propagation based on the isDefaultNavigationPrevented state.
    // If the exit was cancelled, we prevent the default behavior.
    // Otherwise, we allow the event to continue propagating.
    handleEventPropagation(event, isDefaultNavigationPrevented);
    if (isDefaultNavigationPrevented) {
      // The callback returned false, indicating that the exit should not be logged.
      // Before returning we need to log all the interaction events, without
      // applying the navigation event ID.
      for (const interactionEvent of exitInteractionEventList) {
        sendAnalyticsInteractionEvent(interactionEvent);
      }

      return;
    }

    // If we didn't cancel the exit, we log the exit and update the state.
    const element = getEventTarget(event);
    if (element?.tagName === "A") {
      element.setAttribute("href", decoratedUrl);
    }
    setDecoratedUrl(decoratedUrl);
    for (const interactionEvent of exitInteractionEventList) {
      sendAnalyticsInteractionEvent(
        withNavigationEventId(interactionEvent, eventId),
      );
    }
    logExitCallback(decoratedUrl, provider, agencyIds, undefined, eventId);
  }

  const exitUrl = decoratedUrl ?? href;
  return [exitUrl, onExitCallback];
}

function withNavigationEventId(
  event: StructEventParams,
  eventId: string,
): StructEventParams {
  return {
    ...event,
    extraInfo: {
      ...event.extraInfo,
      dimensions: {
        ...event.extraInfo?.dimensions,
        navigationEventId: eventId,
      },
    },
  };
}

/**
 * Validates the exit events generated by the onExit callback.
 * @param interactionEventList - The list of interaction events to validate.
 */
function validateExitEvents(interactionEventList: StructEventParams[]) {
  if (interactionEventList.length > 1) {
    console.error(
      "PROBABLE BUG: onExit callback called sendExitInteractionEvent multiple times. Only one interaction event should be logged per exit link.",
      interactionEventList,
    );
  }
}

/**
 * @param event - The event to get the current target from.
 * If the event is undefined, it returns null.
 * @returns The current target element or null.
 */
function getEventTarget(event?: SyntheticEvent | PressEvent): Element | null {
  if (!event) {
    return null;
  }

  if (event.target instanceof Element) {
    return event.target;
  }

  return null;
}

/**
 * Handles event propagation based on the provided parameters.
 * @param event - The event to handle.
 * @param shouldContinue - Whether the event should continue propagating.
 */
function handleEventPropagation(
  event: SyntheticEvent | PressEvent | undefined,
  isPreventDefault: boolean,
): void {
  if (!event) {
    // If no event is provided, we can't control propagation.
    return;
  }

  if (isPreventDefault) {
    if (
      "preventDefault" in event &&
      typeof event.preventDefault === "function"
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  } else {
    if (
      "continuePropagation" in event &&
      typeof event.continuePropagation === "function"
    ) {
      event.continuePropagation();
    }
  }
}
